import _ from 'lodash-extended';
import moment from 'moment';
import app from 'app';
import { TimePoint, Rectangle, TimeRectangle } from './grid';
import fg from '#/libs/FrameGrid';
import { AVAILABILITY_CURSOR_MAP } from 'v/Story/Timeline/api/model';

export function isNumber(value) {
  return typeof value === 'number' && !isNaN(value);
}

export function invalidTime(time, checkDuration = false) {
  return (
    !isNumber(time) ||
    (checkDuration && time < app.TIME.MAX_INTERASSET_CLEARANCE)
  );
}

export function autocorrectVisualTracks(timelineData) {
  let time = 0;

  for (let n = 0, N = timelineData.visualTracks.length; n < N; n++) {
    const asset = timelineData.visualTracks[n];
    asset.displayDuration = fg.trunc(asset.displayDuration);

    if (invalidTime(asset.displayDuration, true)) {
      app.doctorController.report(
        `TimemachineValidator -> : bad duration ${asset.displayDuration}`
      );
      asset.displayDuration = 1; // assign something default
    }

    asset.displayStartTime = time;
    asset.displayEndTime = fg.trunc(time + asset.displayDuration);
    time = asset.displayEndTime;
  }
}

export function autocorrectAudioTracks(audioTracks) {
  const fields = [
    'displayStartTime',
    'displayEndTime',
    'relativeStartTime',
    'relativeEndTime',
  ];

  for (let n = 0, N = audioTracks.length; n < N; n++) {
    const track = audioTracks[n];
    for (let nn = 0, NN = fields.length; nn < NN; nn++) {
      const field = fields[nn];
      track[field] = fg.trunc(track[field]);
    }
  }
}

/**
 * copy asset durations assuming prop creation side effect of assignment
 * @param target {object}
 * @param source {object}
 */
export function assignDurations(target, source) {
  target.displayStartTime = source.displayStartTime;
  target.displayDuration = source.displayDuration;
  target.displayEndTime = source.displayEndTime;
  if (isNumber(source.startTime)) {
    target.startTime = source.startTime;
  }
  if (isNumber(source.endTime)) {
    target.endTime = source.endTime;
  }
  if (isNumber(source.relativeStartTime)) {
    target.relativeStartTime = source.relativeStartTime;
  }
  if (isNumber(source.relativeEndTime)) {
    target.relativeEndTime = source.relativeEndTime;
  }

  return target;
}

/**
 * calculate minimal amount of rows to sustain tracks visualization
 * @param rowDuration
 * @param maxLayerDuration
 * @returns {number}
 */
export function getNumberOfRows({ rowDuration, maxLayerDuration }) {
  const MIN_FREE_TIME_COEFFICIENT = 0.5;
  const minimum = Math.ceil(maxLayerDuration / rowDuration);
  const coveredTime = minimum * rowDuration;
  const freeTime = coveredTime - maxLayerDuration;
  const minFreeTime = rowDuration * MIN_FREE_TIME_COEFFICIENT;
  let required = minimum;

  if (freeTime <= minFreeTime) {
    required += 1;
  }

  return required;
}

export function getElementRectangle(el) {
  return new Rectangle(
    el.offsetLeft,
    el.offsetTop,
    el.offsetWidth,
    el.offsetHeight
  );
}

/**
 * find time of point
 * @complexity O(log(n))
 * @param timeRectangles {array<TimeRectangle>}
 * @param point {TimePoint}
 * @returns {number|null}
 */
export function point2time(timeRectangles, point) {
  let rv = null;

  const foundIndex = _.findIndexInSorted(timeRectangles, (r) => {
    return point.y < r.p1.y ? -1 : point.y < r.p2.y ? 0 : 1;
  });

  let found = null;
  if (null !== foundIndex) {
    found = timeRectangles[foundIndex];
  } else {
    // find nearest from top or bottom
    const first = timeRectangles[0];
    const last = timeRectangles[timeRectangles.length - 1];
    if (point.y < first.p1.y) {
      found = first;
    } else if (point.y >= last.p2.y) {
      found = last;
    }
  }

  if (found) {
    if (found.atLeft(point)) {
      rv = found.startTime;
    } else if (found.atRight(point)) {
      rv = found.endTime;
    } else {
      const offset = point.x - found.p1.x;
      rv = found.startTime + offset * found.continuum;
    }
  } else {
    app.doctorController.report(`timeless: point: ${point}`);
  }

  point.time = rv;
  return rv;
}

function time2rectangleIndex(time, rowDuration, timeRectangles, pushOverEdge) {
  let rv = null;

  let index = null;
  if (0 === time) {
    index = 0;
  } else {
    index = Math.ceil(time / rowDuration) - 1;
  }

  if (timeRectangles[index]) {
    rv = index;

    if (pushOverEdge) {
      const next = timeRectangles[index + 1];
      if (next && next.startTime === time) {
        ++rv;
      }
    }
  }

  return rv;
}

/**
 * find time coordinates
 * @complexity O(1)
 * @param timeRectangles {array<TimeRectangle>}
 * @param time {number}
 * @param pushOverEdge {boolean}
 * @param rowDuration {number}
 * @param [timePoint] {TimePoint}
 * @returns {TimePoint|null}
 */
export function time2point(
  timeRectangles,
  time,
  pushOverEdge,
  rowDuration,
  timePoint
) {
  const foundIndex = time2rectangleIndex(
    time,
    rowDuration,
    timeRectangles,
    pushOverEdge
  );

  if (null !== foundIndex) {
    if (!timePoint) {
      timePoint = new TimePoint();
    }

    const rectangle = timeRectangles[foundIndex];
    const offset = time - rectangle.startTime;
    timePoint.set(
      rectangle.p1.x + offset / rectangle.continuum,
      rectangle.p1.y,
      time
    );
  } else {
    app.doctorController.report(`pointless: time: ${time}`);
  }

  return timePoint;
}

function createTimeRectangle(p1, p2, state) {
  const rect = new TimeRectangle(
    p1.x,
    p1.y,
    p2.x - p1.x,
    p2.y - p1.y,
    p1.time,
    p2.time
  );

  if (!(state.excludeEmpty && rect.width <= 0)) {
    state.rv.push(rect);
  }

  if (!state.longestRect || state.longestRect.width < p2.x - p1.x) {
    state.longestRect = rect;
  }
}

/**
 * get rectangles covering the time span
 * @param timeRectangles {array<TimeRectangle>}
 * @param startTime {number}
 * @param endTime {number}
 * @param rowDuration {number}
 * @param excludeEmpty {boolean}
 * @returns {array<TimeRectangle>}
 */
export function time2rectangles(
  timeRectangles,
  startTime,
  endTime,
  rowDuration,
  excludeEmpty
) {
  const state = {
    rv: [],
    excludeEmpty,
    longestRect: null,
  };
  // console.time('time2rectangles');
  let p1 = null;
  let p2 = null;
  let cur;
  let endOfLine = false;
  const I = timeRectangles.length;

  // find first p1
  const x = time2rectangleIndex(startTime, rowDuration, timeRectangles, true);
  if (null !== x) {
    cur = timeRectangles[x];
    const offset = startTime - cur.startTime;
    p1 = new TimePoint(cur.p1.x + offset / cur.continuum, cur.p1.y, startTime);
  } else {
    app.doctorController.report(`rectangleless: time ${startTime}`);
    return [];
  }

  // continue to search for p2 and other occurrences
  for (let i = x; i < I; i++) {
    cur = timeRectangles[i];
    const next = timeRectangles[i + 1];

    if (endOfLine) {
      p1 = cur.p1;
      endOfLine = false;
    }

    if (!next && endTime > cur.endTime) {
      // last grid rectangle
      p2 = cur.p2;
      createTimeRectangle(p1, p2, state);
      break;
    } else if (endTime <= cur.endTime) {
      // last rectangle
      const offset = endTime - cur.startTime;
      p2 = new TimePoint(cur.p1.x + offset / cur.continuum, cur.p2.y, endTime);
      createTimeRectangle(p1, p2, state);
      break;
    } else if (next && next.p1.y > cur.p1.y) {
      // end of line
      p2 = cur.p2;
      createTimeRectangle(p1, p2, state);
      endOfLine = true;
    }
  }

  if (state.longestRect) {
    state.longestRect.isLongest = true;
  }
  // console.timeEnd('time2rectangles');
  return state.rv;
}

export function setStyleProperty(el, prop, value) {
  const name = `--tm-${prop}`;
  value = value.toString();
  if (prop.startsWith('px')) {
    value = `${value}px`;
  }
  const propValue = el.style.getPropertyValue(name);
  if (propValue !== value) {
    // as optimization - change only different values
    el.style.setProperty(name, value);
  }
}

export function mouseDistanceMet(e1, e2, distance) {
  return (
    distance <=
    Math.max(Math.abs(e1.pageX - e2.pageX), Math.abs(e1.pageY - e2.pageY))
  );
}

/* @link https://developer.mozilla.org/en/docs/Web/CSS/cursor */
export class CursorManager {
  /**
   * @param cursor {string}
   */
  static set(cursor) {
    if (!CursorManager.prevCursor) {
      CursorManager.prevCursor = 'initial';
    }

    // set new body cursor value only if different
    if (CursorManager.prevCursor !== cursor) {
      document.documentElement.style.setProperty(
        '--tm-cursor',
        cursor,
        cursor !== 'initial' ? 'important' : ''
      );
      CursorManager.prevCursor = cursor;
    }
  }

  /**
   * restore to initial value
   */
  static restore() {
    CursorManager.set('initial');
  }
}

/**
 * format time to string like: "[m]:ss" ":60"  "1:60"
 * @param seconds
 * @return {string}
 */
export function rulerTimeFormat(seconds) {
  const time = {
    seconds: Math.floor(seconds % 60),
    minutes: Math.floor(seconds / 60),
    _: function (i, minutes) {
      if (minutes && i === 0) {
        return '';
      } else if (minutes && i < 10) {
        return i;
      }
      return String(i < 10 ? '0' + i : i);
    },
    toString: function () {
      return this._(this.minutes, true) + ':' + this._(this.seconds);
    },
  };
  return time.toString();
}

/**
 * format time to string like: "m:ss.ms"  "1:60.33"
 * @param seconds
 * @returns {string}
 */
export function trackTimeFormat(seconds) {
  return moment.utc(seconds * 1e3).format('m:ss.SS');
}

/**
 * find item in tracks covering that `time`
 * @complexity O(log(n))
 * @param tracks {array<VisualTrackWrapper|AudioTrackWrapper>}
 * @param time {number}
 * @returns {null|VisualTrackWrapper|AudioTrackWrapper}
 */
export function time2track(tracks, time) {
  let rv = null;

  if (isNumber(time)) {
    const i = _.findIndexInSorted(tracks, (track) => {
      return time < track.data.displayStartTime
        ? -1
        : time < track.data.displayEndTime
        ? 0
        : 1;
    });

    if (null !== i) {
      rv = tracks[i];
    }
  }

  return rv;
}

export function isTimeInsideTrack(time, track) {
  const min = track.data.displayStartTime;
  const max = track.data.displayEndTime;
  return _.between(min, time, max);
}

/**
 * @param grid {array<TimeRectangle>}
 * @param aTime {array<number>} array of times
 * @param distance {number} in pixels
 */
export class ProximityTester {
  constructor(continuum, aTime = [], distance) {
    aTime.sort((a, b) => a - b);
    this.aTime = _.sortedUniq(aTime);

    this.points = new Array(this.aTime.length);
    this.continuum = continuum;

    // fill array of x coordinates with tendrils
    for (let n = 0, N = this.aTime.length, time, prev; n < N; n++) {
      time = this.aTime[n];
      const x = Math.trunc(time / this.continuum);
      const cur = { x, time, tendrilLeft: distance, tendrilRight: distance };
      this.points[n] = cur;

      if (prev) {
        const delta = cur.x - prev.x;
        const limit = 3 * distance;
        if (delta < limit) {
          // ensure at least 33% of non overlapping space
          const newTendril = Math.trunc(delta / 3);
          prev.tendrilRight = newTendril;
          cur.tendrilLeft = newTendril;
        }
      }

      prev = cur;
    }
  }

  /**
   * find closest snap point
   * @complexity O(log(n))
   * @param testTime
   * @return time {null|Number}
   */
  find(testTime) {
    let rv = null;
    const testPoint = Math.trunc(testTime / this.continuum);
    const pointIndex = _.findIndexInSorted(this.points, (point) => {
      const start = point.x - point.tendrilLeft;
      const end = point.x + point.tendrilRight;
      return testPoint < start ? -1 : testPoint < end ? 0 : 1;
    });

    if (null !== pointIndex) {
      rv = this.points[pointIndex].time;
    }

    return rv;
  }
}

/**
 * find times of visual anchors
 * @param visual {Array<VisualTrackWrapper>}
 * @returns {[number]}
 */
export function getVisualAnchorTimes(visual) {
  const rv = [0];
  for (let n = 0, N = visual.length; n < N; n++) {
    rv.push(visual[n].data.displayEndTime);
  }
  return rv;
}

export function getAudioLimit(audioAsset) {
  const duration = fg.trunc(audioAsset.metadata.duration);
  return {
    min: fg.trunc(fg.SPF),
    max: duration,
  };
}

/**
 * @param audios {Array<AudioTrackWrapper>}
 * @return {*[]}
 */
export function getAudioSnapTimesForVideoResizeRight(audios) {
  const rv = [];

  for (let n = 0, N = audios.length; n < N; n++) {
    const audio = audios[n].data;
    rv.push(audio.displayStartTime, audio.displayEndTime);
  }

  return rv;
}

/**
 * find times of autosnap for audio placement for left side
 */
export function getAudioSnapTimesLeft({ trackLayer, exceptGuid }) {
  const rv = [0];

  for (let n = 0, N = trackLayer.visual.length; n < N; n++) {
    rv.push(trackLayer.visual[n].data.displayStartTime);
  }

  rv.push(trackLayer.visualDuration);

  for (let n = 0, N = trackLayer.audio.length; n < N; n++) {
    const audio = trackLayer.audio[n].data;
    if (audio.guid !== exceptGuid) {
      rv.push(audio.displayEndTime);
    }
  }

  return rv;
}

/**
 * find times of autosnap for audio placement for right side
 */
export function getAudioSnapTimesRight({ trackLayer, exceptGuid }) {
  const rv = [];

  for (let n = 0, N = trackLayer.visual.length; n < N; n++) {
    rv.push(trackLayer.visual[n].data.displayEndTime);
  }

  for (let n = 0, N = trackLayer.audio.length; n < N; n++) {
    const audio = trackLayer.audio[n].data;
    if (audio.guid !== exceptGuid) {
      rv.push(audio.displayStartTime);
    }
  }

  return rv;
}

/**
 * find relevant visual anchor time
 * @param proximityTester {ProximityTester}
 * @param time {number}
 * @returns {{outerSpace: boolean, time: null}}
 */
export function findVisualAnchorPoint(proximityTester, time) {
  let found = proximityTester.find(time);
  let outerSpace = false;

  if (null === found) {
    const lastTime = proximityTester.aTime[proximityTester.aTime.length - 1];
    if (time > lastTime) {
      found = lastTime;
      outerSpace = true;
    }
  }

  return {
    time: found,
    outerSpace,
  };
}

/**
 * Timeout that can be aborted but continue to run if still relevant
 */
export class ContinuousTimeout {
  constructor(time, fn) {
    this.time = time;
    this.fn = fn;
    this.timer = null;
  }

  relevant() {
    if (null === this.timer) {
      this.timer = setTimeout(() => {
        this.fn();
        this.timer = null;
      }, this.time);
    }
  }

  abort() {
    if (null !== this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}

export function getResizeAvailability(
  resizeWindow,
  { displayStartTime, displayEndTime }
) {
  return {
    atLeft: {
      canDo: {
        left: displayStartTime > resizeWindow.left.min,
        right: displayStartTime < resizeWindow.left.max,
      },
    },
    atRight: {
      canDo: {
        left: displayEndTime > resizeWindow.right.min,
        right: displayEndTime < resizeWindow.right.max,
      },
    },
  };
}

export function mapAvailability2Cursor(resizeAvailability) {
  const atLeft =
    ((resizeAvailability.atLeft.canDo.left & true) << 1) +
    ((resizeAvailability.atLeft.canDo.right & true) << 0);
  const atRight =
    ((resizeAvailability.atRight.canDo.left & true) << 1) +
    ((resizeAvailability.atRight.canDo.right & true) << 0);

  return {
    left: AVAILABILITY_CURSOR_MAP[atLeft],
    right: AVAILABILITY_CURSOR_MAP[atRight],
  };
}
